import { Link } from 'react-router-dom';

const steps = [
  {
    number: '01',
    icon: 'ri-building-line',
    title: 'Describe Your Business',
    description:
      'Tell the AI ad generator about your business — your industry, target customers, key offer, and the platform you want to advertise on. Takes less than 2 minutes. No technical skills needed.',
    detail: 'Works with Facebook, Instagram, Google, TikTok, and LinkedIn',
    color: 'orange',
  },
  {
    number: '02',
    icon: 'ri-sparkling-2-line',
    title: 'AI Generates Your Complete Ad',
    description:
      'Our AI instantly writes multiple ad variations — headline, hook, body copy, and CTA — plus generates a matching visual creative. Everything is tailored to your business and optimized for your chosen platform.',
    detail: 'Multiple variations generated for A/B testing',
    color: 'amber',
  },
  {
    number: '03',
    icon: 'ri-edit-2-line',
    title: 'Review & Customize',
    description:
      'Preview your AI-generated ads and make any tweaks you want. Swap the headline, change the image, adjust the tone. Our visual editor makes it easy — drag, drop, done.',
    detail: 'Full visual editor included — no design experience needed',
    color: 'rose',
  },
  {
    number: '04',
    icon: 'ri-rocket-line',
    title: 'Download & Launch',
    description:
      'Export your finished ads in the exact dimensions for your platform. Copy the ad text, download your creative, and you\'re ready to launch. From idea to live ad in under 3 minutes.',
    detail: 'Platform-optimized sizes exported automatically',
    color: 'emerald',
  },
];

const colorMap: Record<string, { ring: string; bg: string; text: string; line: string }> = {
  orange: { ring: 'ring-orange-200', bg: 'bg-orange-50', text: 'text-orange-500', line: 'bg-orange-200' },
  amber: { ring: 'ring-amber-200', bg: 'bg-amber-50', text: 'text-amber-500', line: 'bg-amber-200' },
  rose: { ring: 'ring-rose-200', bg: 'bg-rose-50', text: 'text-rose-500', line: 'bg-rose-200' },
  emerald: { ring: 'ring-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-500', line: 'bg-emerald-200' },
};

export default function LandingHowItWorks() {
  return (
    <section id="how-it-works" className="py-20 lg:py-28 bg-white">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-orange-500 mb-3 block">
            Simple Process
          </span>
          <h2 className="text-3xl lg:text-4xl font-black text-stone-900 mb-4 leading-tight">
            How to Create Ads with AI — 4 Easy Steps
          </h2>
          <p className="text-base text-stone-500 leading-relaxed">
            No agency. No design skills. No copywriting background. Just tell our AI ad generator
            what you need, and it handles everything else.
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Vertical connector line */}
          <div className="hidden lg:block absolute left-[2.75rem] top-10 bottom-10 w-0.5 bg-stone-100" />

          <div className="space-y-8">
            {steps.map((step, idx) => {
              const c = colorMap[step.color];
              return (
                <article
                  key={step.number}
                  className="flex flex-col sm:flex-row gap-5 items-start relative"
                >
                  {/* Step number / icon */}
                  <div className={`flex-shrink-0 w-[5.5rem] h-[5.5rem] flex flex-col items-center justify-center rounded-2xl ring-4 ${c.ring} ${c.bg} relative z-10`}>
                    <span className={`text-xs font-black tracking-widest ${c.text} mb-0.5`}>
                      {step.number}
                    </span>
                    <div className={`w-7 h-7 flex items-center justify-center`}>
                      <i className={`${step.icon} text-2xl ${c.text}`} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 sm:pt-4">
                    <h3 className="text-lg font-bold text-stone-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-stone-500 leading-relaxed mb-3">{step.description}</p>
                    <div className="flex items-center gap-2">
                      <i className={`ri-checkbox-circle-line ${c.text} text-sm`} />
                      <span className="text-xs font-semibold text-stone-400">{step.detail}</span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16 p-10 rounded-2xl bg-stone-50 border border-stone-100">
          <h3 className="text-xl font-black text-stone-900 mb-2">
            Ready to create your first AI-generated ad?
          </h3>
          <p className="text-sm text-stone-500 mb-6">
            Join thousands of business owners using the best AI ad generator available today.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Generate Your First Ad — Free
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
